/**
 * A RenderBackend that draws nothing but keeps complete, inspectable state.
 *
 * Used by unit tests and headless tools, and as the runtime fallback when
 * neither WebGPU nor WebGL2 is available (R-1). In that case the rest of the
 * editor (Outliner, Inspector, Content Browser) keeps working.
 */
import { Emitter } from "../core/Emitter";
import type { Unsubscribe } from "../core/Emitter";
import type { FrameInfo } from "../core/FrameLoop";
import type { Aabb } from "../core/math/aabb";
import type { Mat4 } from "../core/math/mat4";
import type {
  CameraDesc,
  CameraPreviewDesc,
  EnvironmentDesc,
  GizmoDesc,
  GizmoEvent,
  PlacementPreviewDesc,
  PostProcessDesc,
  SkyDesc,
  FogDesc,
  ProxyDesc,
  ProxyHandle,
  ShowFlags,
  ViewMode,
} from "./proxies";
import { localBounds } from "./proxies";
import type { RenderBackend, RenderBackendInfo, RenderStats, RenderTheme } from "./RenderBackend";

export interface NullProxy {
  desc: ProxyDesc;
  world: Mat4 | null;
  visible: boolean;
}

export class NullRenderBackend implements RenderBackend {
  readonly info: RenderBackendInfo;
  /** Nothing to show: the viewport says it's unavailable (the null renderer keeps one). */
  readonly canvas: HTMLCanvasElement | null = null;

  // Observable state, for tests.
  readonly proxies = new Map<ProxyHandle, NullProxy>();
  selection: readonly ProxyHandle[] = [];
  /** Each prepareScene call, with how many proxies the scene had then. */
  readonly scenePreparations: { proxies: number }[] = [];
  camera: CameraDesc | null = null;
  environment: EnvironmentDesc | null = null;
  post: PostProcessDesc | null = null;
  sky: SkyDesc | null = null;
  fog: FogDesc | null = null;
  showFlags: ShowFlags | null = null;
  viewMode: ViewMode = "lit";
  theme: RenderTheme | null = null;
  size = { width: 0, height: 0, pixelRatio: 1 };
  framesRendered = 0;
  disposed = false;

  gizmo: GizmoDesc | null = null;
  gizmoActive = false;
  gizmoCancels = 0;
  /** The camera preview being drawn, for tests. */
  cameraPreview: CameraPreviewDesc | null = null;
  /** The last placement preview shown, for tests. */
  placementPreview: PlacementPreviewDesc | null = null;

  /** What `pick` returns; tests script it. */
  pickResult: ProxyHandle | null = null;

  private readonly gizmoEvents = new Emitter<{ gizmo: GizmoEvent }>();
  private readonly boundsEvents = new Emitter<{ bounds: ProxyHandle }>();
  /** Bounds a test said a proxy draws (a model that "arrived"), over its description's. */
  private readonly drawnBounds = new Map<ProxyHandle, Aabb>();

  private nextHandle = 1;

  constructor(info: RenderBackendInfo = { api: "Null" }) {
    this.info = info;
  }

  createProxy(desc: ProxyDesc): ProxyHandle {
    const handle = this.nextHandle++ as ProxyHandle;
    this.proxies.set(handle, { desc, world: null, visible: true });
    return handle;
  }

  updateProxy(handle: ProxyHandle, desc: ProxyDesc): void {
    this.require(handle).desc = desc;
  }

  setProxyTransform(handle: ProxyHandle, world: Mat4): void {
    this.require(handle).world = world;
  }

  setProxyVisible(handle: ProxyHandle, visible: boolean): void {
    this.require(handle).visible = visible;
  }

  destroyProxy(handle: ProxyHandle): void {
    this.require(handle);
    this.proxies.delete(handle);
    this.drawnBounds.delete(handle);
  }

  proxyBounds(handle: ProxyHandle): Aabb | null {
    return this.drawnBounds.get(handle) ?? localBounds(this.require(handle).desc);
  }

  onProxyBoundsChange(listener: (handle: ProxyHandle) => void): Unsubscribe {
    return this.boundsEvents.on("bounds", listener);
  }

  /** Test hook: the proxy now draws `bounds` (as when a streamed model arrives). */
  reportBounds(handle: ProxyHandle, bounds: Aabb): void {
    this.drawnBounds.set(handle, bounds);
    this.boundsEvents.emit("bounds", handle);
  }

  setCamera(camera: CameraDesc): void {
    this.camera = camera;
  }

  setEnvironment(environment: EnvironmentDesc): void {
    this.environment = environment;
  }

  setSky(sky: SkyDesc | null): void {
    this.sky = sky;
  }

  setFog(fog: FogDesc | null): void {
    this.fog = fog;
  }

  setPostProcess(post: PostProcessDesc): void {
    this.post = post;
  }

  setSelection(handles: readonly ProxyHandle[]): void {
    this.selection = handles;
  }

  /** Nothing loads: models arrive as bytes, if at all. */
  modelsLoading(): number {
    return 0;
  }

  /** Records the call: this backend draws nothing, so there's nothing to ready. */
  prepareScene(): Promise<void> {
    this.scenePreparations.push({ proxies: this.proxies.size });
    return Promise.resolve();
  }

  setShowFlags(flags: ShowFlags): void {
    this.showFlags = flags;
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
  }

  applyTheme(theme: RenderTheme): void {
    this.theme = theme;
  }

  setGizmo(gizmo: GizmoDesc | null): void {
    this.gizmo = gizmo;
  }

  onGizmo(listener: (event: GizmoEvent) => void): Unsubscribe {
    return this.gizmoEvents.on("gizmo", listener);
  }

  cancelGizmoDrag(): void {
    this.gizmoCancels++;
    this.gizmoActive = false;
  }

  setCameraPreview(preview: CameraPreviewDesc | null): void {
    this.cameraPreview = preview;
  }

  setPlacementPreview(preview: PlacementPreviewDesc | null): void {
    this.placementPreview = preview;
  }

  /** Simulates a user drag step; tests drive the gizmo through this. */
  emitGizmo(event: GizmoEvent): void {
    this.gizmoActive = event.phase !== "end";
    this.gizmoEvents.emit("gizmo", event);
  }

  resize(width: number, height: number, pixelRatio: number): void {
    this.size = { width, height, pixelRatio };
  }

  render(_frame: FrameInfo): void {
    this.framesRendered++;
  }

  /** Draws nothing, so there's nothing to count. */
  stats(): RenderStats {
    return {
      drawCalls: 0,
      triangles: 0,
      geometries: 0,
      textures: 0,
      pixelRatio: this.size.pixelRatio,
    };
  }

  compile(): Promise<void> {
    return Promise.resolve();
  }

  pick(): Promise<ProxyHandle | null> {
    return Promise.resolve(this.pickResult);
  }

  readPixel(_x: number, _y: number): Promise<readonly [number, number, number, number]> {
    return Promise.resolve([0, 0, 0, 0]);
  }

  /** Nothing is drawn, so there's no thumbnail to give. */
  renderThumbnail(): Promise<null> {
    return Promise.resolve(null);
  }

  dispose(): void {
    this.disposed = true;
  }

  /** Accessing a destroyed or unknown proxy is a bridge bug; fail loudly in tests. */
  private require(handle: ProxyHandle): NullProxy {
    const proxy = this.proxies.get(handle);
    if (!proxy) throw new Error(`Unknown proxy ${handle}`);
    return proxy;
  }
}
